import * as React from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { QueryErrorFallback } from './QueryErrorFallback'
import { useLastBoundaryErrorStore } from '@stores/useLastBoundaryErrorStore'
import { reportFrontendError } from '@services/telemetry/browserTracing'

export type QueryErrorBoundaryProps = {
    children: React.ReactNode
    title?: string
    description?: string
    actions?: React.ReactNode
    defaultShowDetails?: boolean
    onError?: (error: unknown) => void
}

export function QueryErrorBoundary({
        children,
        title,
        description,
        actions,
        defaultShowDetails = false,
        onError,
}: QueryErrorBoundaryProps) {
    const { reset } = useQueryErrorResetBoundary()
    const setLastBoundaryError = useLastBoundaryErrorStore((state) => state.setLastBoundaryError)

    return (
        <ErrorBoundary
            onReset={reset}
            onError={(err, info) => {
                console.error("QueryErrorBoundary caught error:", err);
                console.error("QueryErrorBoundary component stack:", info.componentStack);
                reportFrontendError(err, "React", info.componentStack ?? undefined);
                setLastBoundaryError(err instanceof Error ? err.message : String(err))
                onError?.(err);
            }}
            fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => (
                <QueryErrorFallback
                    error={error}
                    onRetry={resetErrorBoundary}
                    title={title}
                    description={description}
                    actions={actions}
                    defaultShowDetails={defaultShowDetails}
                />
            )}
        >
            {children}
        </ErrorBoundary>
    )
}
